import { Journey } from '@audibene/ta.utils-opportunity'
import { createContext, FC, useContext, useMemo } from 'react'
import { AppointmentType, AvailabilityMode } from '../../../../../../constants'
import { useAvailabilities, useCountry } from '../../../../../../hooks'
import { AvailabilitiesResponse, ResourceId } from '../../../../../../types'

import { useAreResourcesFetching } from '../../../useResources'
import { useAvailabilityParameters } from '../../useAvailabilityParameters'

export type AvailabilityContext = {
  isError: boolean
  data: AvailabilitiesResponse | undefined
  isLoading: boolean
}

const context = createContext<AvailabilityContext>({} as AvailabilityContext)
const { Provider } = context

const AvailabilityProvider: FC = ({ children }) => {
  const params = useAvailabilityParameters()
  const areResourcesFetching = useAreResourcesFetching()
  const country = useCountry()
  const isAvailabilitiesQueryEnabled = Boolean(!areResourcesFetching && params && params.resourceIds.length)

  const availabilityParams = {
    appointmentType: params?.appointment_type as AppointmentType,
    dateEnd: params?.date_end as Date,
    dateStart: params?.date_start as Date,
    durationInMinutes: params?.durationInMinutes as number,
    isCabinOnlyConfiguration: params?.isCabinOnlyConfiguration ?? false,
    isEnabled: isAvailabilitiesQueryEnabled,
    journey: params?.journey as Journey,
    resourceIds: params?.resourceIds as ResourceId[],
    resources: params?.resources || [],
    timeZone: params?.timeZone as string,
  }

  const contextValue = useAvailabilities({
    ...availabilityParams,
    country,
    mode: AvailabilityMode.DEFAULT,
    paramsSettled: Boolean(params),
  })

  const value = useMemo(
    () => ({ data: contextValue.data, isError: contextValue.isError, isLoading: contextValue.isLoading }),
    [contextValue.data, contextValue.isError, contextValue.isLoading]
  )

  return <Provider value={value}>{children}</Provider>
}

const useAvailability = () => useContext(context)

export { AvailabilityProvider, useAvailability }
